"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { Check, FileScan, ScanLine } from "lucide-react";
import { MockChrome } from "./MockWindow";

/**
 * Auto-looping "lab report in, fields out" demo — AI Capture.
 *
 * A scanned report is read, the values it found are lifted into the eCRF with
 * a confidence on each, and a person confirms before any of it counts.
 *
 * Same rule as AiFormBuilderShowcase and for the same reason: the timeline is
 * not allowed to end on a populated form. Extraction is a proposal until a
 * human accepts it, which is what the reviewer of this product was explicit
 * about, so the confirm row is the last beat and the only green thing here.
 */

type Extracted = { field: string; value: string; unit?: string; confidence: number };

const EXTRACTED: Extracted[] = [
  { field: "Haemoglobin", value: "13.4", unit: "g/dL", confidence: 99 },
  { field: "Platelets", value: "228", unit: "10⁹/L", confidence: 98 },
  { field: "ALT", value: "41", unit: "U/L", confidence: 96 },
  { field: "Creatinine", value: "0.9", unit: "mg/dL", confidence: 94 },
];

/** Abstract lines standing in for the scanned page. */
const PAGE_LINES = [82, 64, 92, 48, 74, 88, 56, 70];

export function AiCaptureShowcase() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const reset = () => {
        gsap.set(".cap-scan", { yPercent: -120, opacity: 0 });
        gsap.set(".cap-hit", { opacity: 0 });
        gsap.set(".cap-row", { opacity: 0, x: -6 });
        gsap.set(".cap-confirm", { opacity: 0, y: 8 });
        gsap.set(".cap-status-idle", { opacity: 1 });
        gsap.set(".cap-status-done", { opacity: 0 });
      };
      reset();

      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1.5, onRepeat: reset });

      tl
        // the page is read top to bottom
        .to(".cap-scan", { opacity: 1, duration: 0.2 }, 0.5)
        .to(".cap-status-idle", { opacity: 0, duration: 0.2 }, 0.5)
        .to(".cap-scan", { yPercent: 480, duration: 1.5, ease: "none" }, 0.6)
        .to(".cap-scan", { opacity: 0, duration: 0.2 }, 2.05)
        // values it found light up on the page
        .to(".cap-hit", { opacity: 1, duration: 0.25, stagger: 0.12 }, 0.95)
        // and land in the form, one at a time
        .to(".cap-row", { opacity: 1, x: 0, duration: 0.3, stagger: 0.16, ease: "power2.out" }, 1.25)
        // then it stops and waits for a person
        .to(".cap-status-done", { opacity: 1, duration: 0.3 }, 2.3)
        .to(".cap-confirm", { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 2.5)
        .to({}, { duration: 2.2 });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={root}
      className="overflow-hidden rounded-2xl border border-border/60 bg-surface shadow-[var(--shadow-card)]"
      role="img"
      aria-label="Animated demo: a scanned lab report is read, four values are extracted into the case report form with a confidence score on each, and the result waits for a data manager to confirm it."
    >
      <MockChrome title="AI Capture · Central lab report" />

      <div className="overflow-x-auto p-3 text-[11px] xs:p-4">
        <div className="grid grid-cols-[minmax(0,88px)_minmax(0,1fr)] gap-3 xs:grid-cols-[minmax(0,108px)_minmax(0,1fr)]">
          {/* the scanned page */}
          <div className="relative overflow-hidden rounded-lg border border-border/60 bg-surface-muted/50 p-2">
            <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
              <FileScan className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">Lab.pdf</span>
            </div>
            <div className="mt-2 space-y-1.5">
              {PAGE_LINES.map((w, i) => (
                <div key={i} className="relative">
                  <div className="h-1 rounded-full bg-foreground/12" style={{ width: `${w}%` }} />
                  {i > 0 && i < 5 && (
                    <div
                      className="cap-hit absolute -inset-x-1 -inset-y-0.5 rounded bg-primary/15 ring-1 ring-primary/40"
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>
            {/* the reading sweep */}
            <div
              className="cap-scan pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-transparent via-primary/25 to-transparent"
              aria-hidden="true"
            />
          </div>

          {/* what came out of it */}
          <div className="min-w-0">
            <div className="relative h-3.5">
              <span className="cap-status-idle absolute inset-0 text-[9px] text-muted-foreground">
                Upload the report. It reads it.
              </span>
              <span className="cap-status-done absolute inset-0 inline-flex items-center gap-1 text-[9px] font-semibold text-[#7a3b00]">
                <ScanLine className="h-2.5 w-2.5 shrink-0" />4 values found · none saved yet
              </span>
            </div>

            <div className="mt-1.5 space-y-1">
              {EXTRACTED.map((e) => (
                <div
                  key={e.field}
                  className="cap-row flex items-center justify-between gap-1.5 rounded-md border border-border/50 bg-surface px-1.5 py-1 xs:px-2"
                >
                  <span className="min-w-0 flex-1 truncate text-foreground/85">{e.field}</span>
                  <span className="shrink-0 font-mono text-[10px] font-semibold text-foreground">
                    {e.value}
                    {e.unit && (
                      <span className="ml-0.5 font-normal text-muted-foreground">{e.unit}</span>
                    )}
                  </span>
                  <span
                    className={`hidden shrink-0 rounded-sm px-1 py-px font-mono text-[8px] font-semibold xs:inline ${
                      e.confidence >= 97
                        ? "bg-success/12 text-success"
                        : "bg-accent/25 text-[#7a3b00]"
                    }`}
                  >
                    {e.confidence}%
                  </span>
                </div>
              ))}
            </div>

            <div className="cap-confirm mt-2 flex items-center justify-between gap-2 rounded-md border border-success/30 bg-success/[0.07] px-2 py-1.5">
              <span className="min-w-0 truncate text-[9px] text-foreground/80">
                Checked against the source
              </span>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-1.5 py-px text-[8px] font-semibold text-success">
                <Check className="h-2.5 w-2.5" />
                Confirm
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
